import { capitalize } from "client/utils/capitalize";
import { translateFunc } from "client/utils/translateFunc";
import { Input } from "components/common/Input/Input";
import { Select } from "components/common/Select/Select";
import { cropRatioKeys, CropRatioType } from "store/types/crop";

export interface CropRatioProps {
    ratio: CropRatioType;
    handleCropRatioChange: (ratio: CropRatioType) => void;
    customRatioX: number;
    customRatioY: number;
    handleCustomRatioXChange: (value: number) => void;
    handleCustomRatioYChange: (value: number) => void;
}

export const CropRatio = ({
    ratio,
    handleCropRatioChange,
    customRatioX,
    customRatioY,
    handleCustomRatioXChange,
    handleCustomRatioYChange,
}: CropRatioProps) => {
    const cropRatios: Record<CropRatioType, string> = {
        freeform: translateFunc("freeform"),
        original: translateFunc("original"),
        custom: translateFunc("custom"),
        "1:1": `1:1 (${capitalize(translateFunc("square"))})`,
        "4:3": `4:3 (${capitalize(translateFunc("monitor"))})`,
        "14:9": "14:9",
        "16:9": `16:9 (${capitalize(translateFunc("wide-screen"))})`,
        "16:10": "16:10",
        "2:1": "2:1",
        "3:1": `3:1 (${capitalize(translateFunc("panaroma"))})`,
        "4:1": "4:1",
        "3:2": `3:2 (35mm ${translateFunc("film")})`,
        "5:4": "5:4",
        "7:5": "7:5",
        "19:10": "19:10",
        "21:9": `21:9 (${capitalize(translateFunc("cinemascope"))})`,
        "32:9": `32:9 (${capitalize(translateFunc("super-ultra-wide"))})`,
        "180:180": `${translateFunc("fb-profile")} 170x170`,
        "820:312": `${translateFunc("fb-cover")} 820x312`,
        "1200:900": `${translateFunc("fb-post")} 1200x900`,
        "1280:720": `${translateFunc("fb-ad")} 1280x720`,
        "110:110": `${translateFunc("insta-profile")} 110x110`,
        "320:320": `${translateFunc("insta-post")} 320x320`,
        "1080:1920": `${translateFunc("insta-story")} 1080x1920`,
        "400:400": `${translateFunc("twitter-profile")} 400x400`,
        "1500:1500": `${translateFunc("twitter-header")} 1500x1500`,
        "1024:512": `${translateFunc("twitter-image")} 1024x512`,
        "1200:628": `${translateFunc("twitter-card")} 1200x628`,
        "1200:675": `${translateFunc("twitter-ad")} 1200x675`,
        "800:800": `${translateFunc("yt-profile")} 800x800`,
        "2560:1440": `${translateFunc("yt-channel-art")} 2560x1440`,
        "5120:2880": `${translateFunc("yt-thumb")} 1280x720`,
        "1024:768": `${translateFunc("web-mini")} 1024x768`,
        "1280:800": `${translateFunc("web-small")} 1280x800`,
        "1366:768": `${translateFunc("web-common")} 1366x768`,
        "1440:900": `${translateFunc("web-medium")} 1440x900`,
        "1920:1080": `${translateFunc("full-hd")} 1920x1080`,
        "3840:2160": `${translateFunc("ultra-hd")} 4x 3840x2160`,
        "2480:3508": `${translateFunc("paper-a-4")} 2480x3508`,
        "1748:2480": `${translateFunc("paper-a-5")} 1748x2480`,
        "1280:1748": `${translateFunc("paper-a-6")} 1280x1748`,
        "2400:3300": `${translateFunc("paper-letter")} 2400x3300`,
    };

    return (
        <div className="flex flex-col gap-4">
            <Select
                options={cropRatioKeys.map((c) => c)}
                getOptionValue={(o) => o}
                value={ratio}
                handleClick={handleCropRatioChange}
                label={translateFunc("aspect-ratio")}
                getOptionLabel={(option) => cropRatios[option]}
                testKey="crop-settings-crop-ratio"
            />
            {ratio === "custom" && (
                <div className="flex gap-2">
                    <div className="">
                        <div className="capitalize mb-2">{`${capitalize(translateFunc("ratio"))} (X)`}</div>
                        <Input
                            className="w-full"
                            type="number"
                            acceptFloat={true}
                            min={0}
                            value={customRatioX}
                            onChange={(e) => handleCustomRatioXChange(+e.target.value)}
                            data-testid="crop-settings-left"
                        />
                    </div>
                    <div className="">
                        <div className="capitalize mb-2">{`${capitalize(translateFunc("ratio"))} (Y)`}</div>
                        <Input
                            className="w-full"
                            type="number"
                            min={0}
                            acceptFloat={true}
                            value={customRatioY}
                            onChange={(e) => handleCustomRatioYChange(+e.target.value)}
                            data-testid="crop-settings-top"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
